import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { AlertService } from '../../../../services/alert.service';
import { ChannelsService } from '../../../../services/channels.service';
import Icon from '../../../../components/forms/icon.component';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import Switch from '../../../../components/forms/switch.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';

interface OperatorField {
  rcs_output: string;
  priority: string;
}

interface ChannelModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
  agents: any;
  provider: any;
}

interface AgentField {
  id: number;
  rcs_output: string;
  priority: string;
}

interface FormValues {
  _id: string;
  name: string;
  is_international: boolean;
  [key: string]: any;
}

function AgentsModal({ payload, agents, show, onRequestClose }: ChannelModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const rcs_type = [
    { value: 'CONVERSATIONAL', label: t('channels.rcs.conversational') },
    { value: 'BASIC', label: t('channels.rcs.basic') },
    { value: 'SINGLE', label: t('channels.rcs.single') }
  ];
  const [operatorFieldsTelcel, setOperatorFieldsTelcel] = useState<AgentField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsMovistar, setOperatorFieldsMovistar] = useState<AgentField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsAtt, setOperatorFieldsAtt] = useState<AgentField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);
  const [operatorFieldsOther, setOperatorFieldsOther] = useState<AgentField[]>([
    { id: 0, rcs_output: '', priority: '' }
  ]);

  const [atLeastOneFilled, setAtLeastOneFilled] = useState(false);

  const defaultValues: FormValues = {
    _id: '',
    name: '',
    type: '',
    is_international: false
  };

  const {
    register,
    watch,
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const international = watch('is_international');

  const checkIfAtLeastOneFilled = () => {
    if (international) {
      const isFilled = operatorFieldsOther.some(
        (field) => field.rcs_output !== '' && field.priority !== ''
      );
      setAtLeastOneFilled(isFilled);
    } else {
      const operators: OperatorField[][] = [
        operatorFieldsTelcel,
        operatorFieldsMovistar,
        operatorFieldsAtt,
        operatorFieldsOther
      ];
      const isAtLeastOneFilled = operators.some((fields) =>
        fields.some((field) => field.rcs_output !== '' && field.priority !== '')
      );
      setAtLeastOneFilled(isAtLeastOneFilled);
    }
  };

  const areRelevantFieldsFilled = () => {
    const operators: { fields: OperatorField[]; relevant: boolean }[] = [
      { fields: operatorFieldsTelcel, relevant: false },
      { fields: operatorFieldsMovistar, relevant: false },
      { fields: operatorFieldsAtt, relevant: false },
      { fields: operatorFieldsOther, relevant: true }
    ];

    const allFieldsFilled = (fields: OperatorField[]) =>
      fields.every((field) => field.rcs_output !== '' && field.priority !== '');
    const isRelevant = (fields: OperatorField[]) =>
      fields.some((field) => field.rcs_output !== '' || field.priority !== '');

    if (international) {
      return !isRelevant(operatorFieldsOther) || allFieldsFilled(operatorFieldsOther);
    } else {
      return operators.every(({ fields }) => !isRelevant(fields) || allFieldsFilled(fields));
    }
  };

  const handleAgentsChange = (operatorType: string, id: number, value: string) => {
    const operators: Record<
      string,
      { fields: AgentField[]; setFields: React.Dispatch<React.SetStateAction<AgentField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };

    const { fields: agentFields, setFields } = operators[operatorType];
    let updatedFields = agentFields.map((field) =>
      field.id === id ? { ...field, rcs_output: value } : field
    );

    const activeFields = updatedFields.filter((field) => field.rcs_output !== '');

    if (activeFields.length === 1 && activeFields[0].id === id) {
      updatedFields = updatedFields.map((field) =>
        field.id === id ? { ...field, priority: '100' } : field
      );
    } else if (activeFields.length > 1) {
      updatedFields = recalculatePriorities(updatedFields);
    } else {
      updatedFields = updatedFields.map((field) =>
        field.id === id ? { ...field, priority: '' } : field
      );
    }
    setFields(updatedFields);
  };

  const recalculatePriorities = (fields: AgentField[]) => {
    if (fields.length === 0) {
      return [];
    }
    const newPriority = Math.floor(100 / fields.length);
    let remainingPercentage = 100;

    return fields.map((field, index) => {
      let currentPriority = newPriority;
      if (index === fields.length - 1) {
        currentPriority = remainingPercentage;
      }
      remainingPercentage -= currentPriority;
      return { ...field, priority: currentPriority.toString() };
    });
  };

  const handlePriorityChange = (operatorType: string, id: number, value: string) => {
    const operators: Record<
      string,
      { fields: AgentField[]; setFields: React.Dispatch<React.SetStateAction<AgentField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };

    const operator = operators[operatorType];
    if (!operator) {
      console.error(`Operator type ${operatorType} not found.`);
      return;
    }

    const { fields: agentFields, setFields } = operator;
    setFields(
      agentFields.map((field) => (field.id === id ? { ...field, priority: value } : field))
    );
  };

  const removeAgentField = (operatorType: string, id: number) => {
    const operators: Record<
      string,
      { fields: AgentField[]; setFields: React.Dispatch<React.SetStateAction<AgentField[]>> }
    > = {
      telcel: { fields: operatorFieldsTelcel, setFields: setOperatorFieldsTelcel },
      movistar: { fields: operatorFieldsMovistar, setFields: setOperatorFieldsMovistar },
      att: { fields: operatorFieldsAtt, setFields: setOperatorFieldsAtt },
      other: { fields: operatorFieldsOther, setFields: setOperatorFieldsOther }
    };
    const { fields: agentFields, setFields } = operators[operatorType];
    if (agentFields?.length > 1) {
      const updatedFields = agentFields.filter((field) => field.id !== id);
      const fieldsWithRecalculatedPriorities = recalculatePriorities(updatedFields); // Recalcula
      setFields(fieldsWithRecalculatedPriorities);
    } else {
      setFields([{ id: 0, rcs_output: '', priority: '' }]);
    }
    checkIfAtLeastOneFilled();
  };

  const renderAgentFields = (operatorType: string, agentFields: AgentField[]) => {
    const firstRowFilled =
      agentFields?.length > 0 && agentFields[0].rcs_output !== '' && agentFields[0].priority !== '';
    const agentOptionsCount = agents[operatorType]?.length;
    let maxRows = 10;
    if (agentOptionsCount <= 10) {
      maxRows = agentOptionsCount;
    }
    return (
      <>
        {agentFields.map((field) => {
          const selectedAgents = agentFields
            .map((f) => f.rcs_output)
            ?.filter((agent) => agent !== field.rcs_output);
          const agentOptions = international
            ? agents['other']
                ?.filter((agent: any) => agent.is_international)
                .filter((agent: any) => !selectedAgents.includes(agent._id))
                .map((agent: any) => ({
                  value: agent._id,
                  label: agent.name
                }))
            : agents[operatorType]
                ?.filter((agent: any) => !agent.is_international)
                .filter((agent: any) => !selectedAgents.includes(agent._id))
                .map((agent: any) => ({
                  value: agent._id,
                  label: agent.name
                }));
          const priorityOptions = Array.from({ length: 100 }, (_, i) => ({
            value: `${i + 1}`,
            label: `${i + 1}`
          }));
          return (
            <div className="form-group row" key={field.id}>
              <div className="col-md-7">
                <label className="form-label">{t('channels.rcs.outputs')}</label>
                <Select
                  value={agentOptions?.find((agent: any) => agent.value === field.rcs_output)}
                  onChange={(selectedOption) =>
                    handleAgentsChange(
                      operatorType,
                      field.id,
                      selectedOption ? selectedOption.value : ''
                    )
                  }
                  options={agentOptions}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                  }
                  placeholder={t('channels.rcs.select-output')}
                  className="form-select-custom"
                  classNamePrefix="form-select-custom"
                  isClearable
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('channels.rcs.modal-C-seccion2-priority')}</label>
                <Select
                  value={priorityOptions?.find((option) => option.value === field.priority)}
                  onChange={(selectedOption) =>
                    handlePriorityChange(
                      operatorType,
                      field.id,
                      selectedOption ? selectedOption.value : ''
                    )
                  }
                  options={priorityOptions}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                  }
                  className="form-select-custom"
                  classNamePrefix="form-select-custom"
                  placeholder={t('channels.rcs.modal-C-seccion2-priority')}
                  isClearable
                />
              </div>
              <div className="col-md-1 d-flex align-items-center mb-4">
                {agentFields?.length > 1 && (
                  <div className="col-md-1 mt-0">
                    <a
                      className="btn btn-light-danger mt-3 mt-md-8"
                      onClick={() => removeAgentField(operatorType, field.id)}
                    >
                      <i className="ki-duotone ki-trash fs-2">
                        <span className="path1" />
                        <span className="path2" />
                        <span className="path3" />
                        <span className="path4" />
                        <span className="path5" />
                      </i>
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const registerAgent = async (data: any) => {
    if (!atLeastOneFilled) {
      AlertService.toastError(t('channels.rcs.modal-C-errorValid'));
      return;
    }
    const filterAndMapOperators = (operatorFields: any[]) => {
      if (!operatorFields) return [];
      return operatorFields
        .filter((field: OperatorField) => field.rcs_output !== '' && field.priority !== '')
        .map(({ rcs_output, priority }) => ({
          output: rcs_output,
          priority: Number(priority)
        }));
    };
    const filteredTelcel = international ? [] : filterAndMapOperators(operatorFieldsTelcel);
    const filteredMovistar = international ? [] : filterAndMapOperators(operatorFieldsMovistar);
    const filteredAtt = international ? [] : filterAndMapOperators(operatorFieldsAtt);
    const filteredOther = filterAndMapOperators(operatorFieldsOther);
    let formData: any;

    if (international) {
      formData = {
        name: data.name,
        type: data.type,
        is_international: true,
        international: filteredOther,
        ...(payload != null && { _id: data._id })
      };
    } else {
      formData = {
        name: data.name,
        type: data.type,
        is_international: false,
        telcel: filteredTelcel,
        movistar: filteredMovistar,
        att: filteredAtt,
        other: filteredOther,
        ...(payload != null && { _id: data._id })
      };
    }

    const messageQuestion = payload
      ? '¿Deseas editar este agente?'
      : '¿Deseas registrar este nuevo agente?';

    const isConfirmed = await AlertService.question(
      t('general.atention'),
      messageQuestion + '<br><br>',
      () => Promise.resolve(true)
    );

    if (!isConfirmed) return;
    try {
      if (payload != null) {
        await ChannelsService.updateAgentsRCS(formData);
        AlertService.toastSuccess(t('channels.rcs.modal-C-succesEdit'));
      } else {
        await ChannelsService.addAgentsRCS(formData);

        AlertService.toastSuccess(t('channels.rcs.modal-C-succes'));
      }
      await onRequestClose(true);
      resetForm();
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const checkPrioritiesSum = () => {
    const sumOperatorPriorities = (fields: AgentField[]) => {
      const activeFields = fields.filter(
        (field) => field.rcs_output !== '' && field.priority !== ''
      );
      if (activeFields.length === 0) {
        return true;
      }
      const totalPriority = activeFields.reduce((sum, field) => sum + Number(field.priority), 0);
      return totalPriority === 100;
    };

    if (international) {
      return sumOperatorPriorities(operatorFieldsOther);
    } else {
      const telcelValid = sumOperatorPriorities(operatorFieldsTelcel);
      const movistarValid = sumOperatorPriorities(operatorFieldsMovistar);
      const attValid = sumOperatorPriorities(operatorFieldsAtt);
      const otherValid = sumOperatorPriorities(operatorFieldsOther); // 'other' también aplica para nacional si tiene salidas
      return telcelValid && movistarValid && attValid && otherValid;
    }
  };

  const resetFields = () => {
    const initialFields = [{ id: 0, rcs_output: '', priority: '' }];
    setOperatorFieldsTelcel(initialFields);
    setOperatorFieldsMovistar(initialFields);
    setOperatorFieldsAtt(initialFields);
    setOperatorFieldsOther(initialFields);
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
    setOperatorFieldsTelcel([]);
    setOperatorFieldsMovistar([]);
    setOperatorFieldsAtt([]);
    setOperatorFieldsOther([]);
  };

  useEffect(() => {
    if (show) {
      resetFields();
    }
  }, [show]);

  useEffect(() => {
    checkIfAtLeastOneFilled();
  }, [
    operatorFieldsTelcel,
    operatorFieldsMovistar,
    operatorFieldsAtt,
    operatorFieldsOther,
    international
  ]);

  useEffect(() => {
    const updateOperatorFields = (field: string, data: any[]) => {
      if (data?.length > 0) {
        return data.map((item: any, index: number) => ({
          id: index,
          rcs_output: item.output,
          priority: item.priority.toString()
        }));
      } else {
        return [{ id: 0, rcs_output: '', priority: '' }];
      }
    };

    if (payload) {
      const telcelFields = updateOperatorFields('telcel', payload.telcel);
      setOperatorFieldsTelcel(telcelFields);
      const movistarFields = updateOperatorFields('movistar', payload.movistar);
      setOperatorFieldsMovistar(movistarFields);
      const attFields = updateOperatorFields('att', payload.att);
      setOperatorFieldsAtt(attFields);
      const otherData = payload.is_international ? payload.international : payload.other;
      const otherFields = updateOperatorFields('other', otherData);
      setOperatorFieldsOther(otherFields);
      reset({
        _id: payload?._id || defaultValues._id,
        name: payload?.name ?? defaultValues.name,
        type: typeof payload?.type === 'string' ? payload.type.toUpperCase() : defaultValues.type,
        is_international: payload?.is_international ?? defaultValues.is_international
      });
    }
  }, [payload]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <i className="ki-duotone ki-cross fs-2x">
                <span className="path1" />
                <span className="path2" />
              </i>
            </div>
          </div>
          <form onSubmit={handleSubmit(registerAgent)}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {watch('_id') ? (
                    <>
                      {t('channels.rcs.modal-C-titleEdit2')}
                      <span className="link-primary fw-bold ms-2">{payload?.name}</span>
                    </>
                  ) : (
                    <> {t('channels.rcs.modal-C-title2')}</>
                  )}
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row mt-10">
                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <Title
                    title={t('channels.rcs.modal-C-seccion1-title')}
                    subtitle={t('channels.rcs.modal-C-seccion1-Subtitle2')}
                    icon="ki-pencil"
                    type="info"
                  />
                  <div className="fv-row mb-13">
                    <Input
                      label={t('channels.rcs.tc-name')}
                      requiredIndicator="required"
                      placeholder={t('channels.rcs.modal-C-seccion1-placeholder')}
                      errors={errors.name}
                      {...register('name', { required: true })}
                    />
                    <label className="form-label required">{t('general.type')}</label>
                    <Controller
                      control={control}
                      name="type"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            isDisabled={watch('profile') === ''}
                            options={rcs_type}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={t('channels.rcs.select-rcs')}
                            onChange={(selectedOption) => {
                              onChange(selectedOption ? selectedOption.value : null);
                              setValue('type', selectedOption ? selectedOption.value : '');
                            }}
                            value={rcs_type?.find((rcs: any) => rcs.value === value) || null}
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                  <div className="row mb-20">
                    <div className="d-flex flex-stack">
                      <div className="d-flex">
                        <span className="d-flex align-items-center">
                          <i className="bi bi-globe-americas fs-3x hover-scale me-2 me-6"></i>
                        </span>
                        <div className="d-flex flex-column">
                          <span className="fs-5 text-dark text-hover-primary fw-bold">
                            {t('channels.rcs.international')}
                          </span>
                          <div className="fs-6 fw-semibold text-gray-400 mw-300px">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t('channels.rcs.modal-C-seccion1-subtitle-inter')
                              }}
                            ></span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-end">
                        <div className="form-check form-check-solid form-check-custom form-switch">
                          <Switch {...register('is_international')} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 rounded h-100 p-3">
                  <Title
                    title={
                      <span className="d-flex align-items-center">
                        {t('channels.rcs.modal-C-seccion2-title')}
                      </span>
                    }
                    subtitle={t('channels.rcs.modal-C-seccion2-Subtitle')}
                    icon="ki-satellite"
                    longDescription={
                      'La asignación de prioridades se realiza por operador. Si asignas varias salidas a un operador, la suma total de las prioridades entre todas las salidas seleccionadas debe ser igual a 100.'
                    }
                  />
                  {!international && (
                    <>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img src="/assets/media/operators/telcel.png" alt="imageCh" width="70px" />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/telcel-dark.png"
                          alt="imageCh"
                          width="70px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderAgentFields('telcel', operatorFieldsTelcel)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img
                          src="/assets/media/operators/movistar.png"
                          alt="imageCh"
                          width="80px"
                        />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/movistar-dark.png"
                          alt="imageCh"
                          width="80px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderAgentFields('movistar', operatorFieldsMovistar)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img src="/assets/media/operators/att.png" alt="imageCh" width="70px" />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/att-dark.png"
                          alt="imageCh"
                          width="70px"
                        />
                      </div>
                      <div className="fv-row mb-10">
                        {renderAgentFields('att', operatorFieldsAtt)}
                      </div>
                      <div className="fw-bold fs-6 theme-light-show mb-2">
                        <img src="/assets/media/operators/altan.png" alt="imageCh" width="60px" />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img src="/assets/media/operators/altan.png" alt="imageCh" width="60px" />
                      </div>
                      <div className="fv-row mb-10">
                        {renderAgentFields('other', operatorFieldsOther)}
                      </div>
                    </>
                  )}
                  {international && (
                    <>
                      <div className="fw-bold fs-6 theme-light-show">
                        <img
                          src="/assets/media/operators/altan.png"
                          alt="imageCh"
                          width="60px"
                          height="30px"
                        />
                      </div>
                      <div className="fw-bold fs-6 theme-dark-show">
                        <img
                          src="/assets/media/operators/altan.png"
                          alt="imageCh"
                          width="60px"
                          height="30px"
                        />
                      </div>
                      <div className="fv-row mb-10 mt-5">
                        {renderAgentFields('other', operatorFieldsOther)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={
                    !atLeastOneFilled ||
                    !isValid ||
                    isSubmitting ||
                    !areRelevantFieldsFilled() ||
                    !checkPrioritiesSum()
                  }
                  isLoading={isSubmitting}
                >
                  {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgentsModal;
